<template>
  <div class="trainee-list-modal-overlay">
    <transition name="modal-expand">
      <div v-if="isVisible" class="trainee-list-modal" @click.stop>
        <div class="modal-content">
          <font-awesome-icon class="window-close-icon" :icon="['fas', 'window-close']" @click="close" />
          <h1>Add Trainee</h1>
          <div class="add">
            <form @submit.prevent="addUser">
              <div class="form-group">
                <label for="name">First Name: *</label>
                <input type="text" class="form-control rounded" id="name" name="name" placeholder="Enter First Name"
                  v-model="newUser.first_name" />
              </div>
              <div class="form-group">
                <label for="name">Last Name: *</label>
                <input type="text" class="form-control rounded" id="name" name="name" placeholder="Enter Last Name"
                  v-model="newUser.last_name" />
              </div>
              <div class="form-group">
                <label for="email">Email: *</label>
                <input type="text" class="form-control rounded" id="email" name="email"
                  placeholder="Enter Email Address" v-model="newUser.email" />
              </div>
              <div class="form-group">
                <label for="license_no">License No:</label>
                <input type="text" class="form-control rounded" id="license_no" name="license_no"
                  title="License No may only contain letters and numbers"
                  placeholder="Professional ID" v-model="newUser.license_no" />
              </div>
              <div class="form-group">
                <label for="date_of_birth">Date of Birth:</label>
                <input type="date" class="form-control rounded" id="date_of_birth" name="date_of_birth"
                  placeholder="Enter Date of Birth" v-model="newUser.date_of_birth" />
              </div>
              <div class="form-group">
                <label for="place_of_birth">Place of Birth:</label>
                <input type="text" class="form-control rounded" id="place_of_birth" name="place_of_birth"
                  placeholder="Enter the Place of Birth" v-model="newUser.place_of_birth" />
              </div>
              <div class="form-group">
                <label for="category">Category:</label>
                <select class="form-control rounded" id="category" name="category" v-model="newUser.category">
                  <option value=""></option>
                  <option value="B1">B1</option>
                  <option value="B2">B2</option>
                  <option value="B1 B2">B1 B2</option>
                </select>
              </div>
              <div class="form-group checkbox-group">
                <label class="checkbox-label">
                  <input type="checkbox" v-model="useTempPassword" />
                  Issue temporary password (email trainee)
                </label>
              </div>
              <div v-if="tempPassword" class="alert alert-info" style="white-space: pre-wrap;">
                <span><b>Temp password (shown once):</b> {{ tempPassword }} <br>Copy the temp password now — it won't be shown again after closing or clicking 'Add Trainee'.</span>
              </div>
              <div v-if="!useTempPassword">
                <div class="form-group">
                  <label for="password">Password:</label>
                  <input :type="showPassword ? 'text' : 'password'" class="form-control rounded" id="password"
                    name="password" placeholder="Create Password" v-model="newUser.password" />
                  <font-awesome-icon class="eye-icon" :icon="['far', showPassword ? 'eye' : 'eye-slash']"
                    @click="toggleShowPassword" />
                </div>
                <div v-if="passwordRules && !passwordValid" class="password-rules" style="margin-top: -20px; margin-left: 50px;">
                  <small>{{ passwordRules.description }}</small>
                </div>
                <div class="form-group">
                  <label for="reenteredPassword">Re-enter Password:</label>
                  <input :type="showPassword ? 'text' : 'password'" class="form-control rounded" id="reenteredPassword"
                    name="reenteredPassword" placeholder="Re-enter Password" v-model="newUser.reenteredPassword" />
                </div>
              </div>
              <div v-if="showAlert" class="alert alert-danger error-message" role="alert">
                <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
              </div>
              <div>
                <button class="btn btn-primary rounded" style="margin-top: 10px;" type="submit" :disabled="processing"
                  id="addTraineeButton">{{ processing ? 'Processing...' : 'Add Trainee' }}</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AddTraineeModal',
  props: {
    users: Array,
    user: { type: Object, default: null }, // null = Add mode
  },
  data() {
    return {
      newUser: {
        first_name: '',
        last_name: '',
        roles: ['trainee'],
        password: '',
        reenteredPassword: '',
        email: '',
        license_no: '',
        category: '',
      },
      showAlert: false,
      currentTime: '',
      errorMessages: {},
      errorMessage: '',
      loggedInUser: '',
      showPassword: false,
      isVisible: false,
      useTempPassword: false,
      tempPassword: '',
      tempLoading: false,
      traineeCreated: false,
      createdEmailSent: false,
      passwordRules: null,
      processing: false,
    };
  },
  computed: {
    passwordChecklist() {
      const pw = this.newUser.password || '';
      const min = this.passwordRules?.min ?? 8;

      return {
        min: pw.length >= min,
        letter: /[A-Za-z]/.test(pw),
        number: /\d/.test(pw),
      };
    },
    passwordValid() {
      return (
        this.passwordChecklist.min &&
        this.passwordChecklist.letter &&
        this.passwordChecklist.number
      );
    },
  },
  watch: {
    useTempPassword(newVal) {
      if (newVal) {
        this.generateTempPassword();
      } else {
        this.tempPassword = '';
      }
    }
  },
  methods: {
    toggleShowPassword() {
      this.showPassword = !this.showPassword;
    },
    generateTempPassword() {
      const letters = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
      const numbers = '23456789'; // no confusing chars
      const all = letters + numbers;

      // 1 letter + 1 number guaranteed
      let password =
        letters[Math.floor(Math.random() * letters.length)] +
        numbers[Math.floor(Math.random() * numbers.length)];

      // Fill remaining length
      for (let i = 2; i < 8; i++) {
        password += all[Math.floor(Math.random() * all.length)];
      }

      // Shuffle so pattern isn’t predictable
      this.tempPassword = password
        .split('')
        .sort(() => Math.random() - 0.5)
        .join('');

      // Clear manual password fields
      this.newUser.password = '';
      this.newUser.reenteredPassword = '';
    },
    close() {
      this.$emit('close');
    },
    async addUser() {
      if (this.processing) return;
      this.processing = true;
      // If using temp password, make sure it's generated
      if (this.useTempPassword && !this.tempPassword) {
        this.generateTempPassword();
      }

      // If NOT using temp password, validate manual passwords
      if (!this.useTempPassword) {
        if (this.newUser.password || this.newUser.reenteredPassword) {
          if (this.newUser.password !== this.newUser.reenteredPassword) {
            this.errorMessage = 'Passwords do not match';
            this.showAlert = true;
            this.processing = false;
            return;
          }
        }
      }
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        // Choose which password to send to /api/users
        const passwordToUse = this.useTempPassword ? this.tempPassword : this.newUser.password;
        
        const response = await axios.post(
          '/api/users',
          {
            first_name: this.newUser.first_name,
            last_name: this.newUser.last_name,
            roles: ['trainee'],
            email: this.newUser.email,
            password: passwordToUse,
            password_confirmation: this.useTempPassword ? passwordToUse : this.newUser.reenteredPassword,
            license_no: this.newUser.license_no,
            category: this.newUser.category,
            is_active: 1,
          },
          { headers }
        );

        // If temp password enabled, email it
        if (this.useTempPassword) {
          await axios.post(
            '/api/contact',
            {
              first_name: this.newUser.first_name,
              last_name: this.newUser.last_name,
              email: this.newUser.email,
              UIN: this.newUser.license_no,
              temporary_password: this.tempPassword,
            },
            { headers }
          );
        }
        if (response.status === 200 || response.status === 201) {
          window.alert('User added Successfully');
          this.newUser = {
            first_name: '',
            last_name: '',
            roles: ['trainee'],
            email: '',
            password: '',
            reenteredPassword: '',
            license_no: '',
            category: '',
          };
          this.useTempPassword = false;
          this.tempPassword = '';
          this.errorMessage = '';
          this.showAlert = false;
          this.close();
        }
        //this.$router.push({ name: 'ViewTraineeTable' });
      } catch (error) {
        console.error('Error adding User:', error.response);
        this.processing = false;

        if (error.response && error.response.data) {
          this.errorMessage = error.response.data.message || 'An error occurred while adding the user.';
        } else {
          this.errorMessage = 'An error occurred while adding the user.';
        }

        this.showAlert = true; // Ensure the alert is shown
      } finally {
        this.processing = false;
      }
    },
    goBack() {
      this.$router.push({ name: 'ViewTraineeTable' });
    },
    async fetchPasswordRules() {
      try {
        const res = await axios.get('/api/auth/password-rules');
        this.passwordRules = res.data;
      } catch (e) {
        console.error('Failed to fetch password rules', e);
        this.passwordRules = { description: 'Minimum 8 characters, at least 1 letter and 1 number.', min: 8, requires: { letters: true, numbers: true } };
      }
    },
  },
  async mounted() {
    this.isVisible = true;
    await this.fetchPasswordRules();
  },
};
</script>

<style scoped>
.trainee-list-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  /* Semi-transparent background */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  /* Ensure the modal is on top of other content */
}

h2 {
  margin-top: 30px;
  font-size: 30px;
  margin-bottom: 30px;
}

/* Style your modal content */
.trainee-list-modal {
  background-color: #fff;
  border-radius: 5px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  width: 80%;
  max-width: 800px;
  /* Limit the maximum width of the modal */
  padding: 20px;
  position: relative;
  max-height: 95%;
}

.modal-content {
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
  padding-left: 15px;
  padding-right: 15px;
}

.add {
  margin-top: 0;
  height: 90%;
}

button.btn:hover {
  background-color: #0c1a61;
  transition: transform 0.2s, box-shadow 0.2s;
}


.form-control.rounded {
  height: 40px;
}

.form-group {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.form-group label {
  width: 150px;
  margin-right: 30px;
  text-align: right;
  margin-bottom: 0px;
}

.form-group input.form-control,
.form-group select.form-control {
  flex: 1;
  width: auto;
  margin-left: 0;
  margin-bottom: 0px;
}

.form-control {
  border-radius: 5px;
}

/* Match input height */
:root {
  --input-height: 40px;
}

/* Checkbox row aligns like other form groups */
.checkbox-group {
  align-items: center;
}

/* Make checkbox label look like an input */
.checkbox-label {
  width: 350px !important;
  margin-left: 170px;
}

/* Checkbox itself */
.checkbox-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  padding-top: 10px;                       /* remove default spacing */
  margin-left: 10px;
  margin-right: 10px;
  accent-color: #0d6efd;
  flex-shrink: 0;
  margin-bottom: 0;
}


#date_of_birth,
#end_date {
  flex: 0 0 180px;
  /* don't grow, don't shrink, fixed 100px */
  width: 180px;
}

h1 {
  padding-top: 10px;
  margin-bottom: 30px;
  margin-left: 30px;
  font-size: 2rem;
  text-align: left;
}

.alert-danger {
  color: #ff0019;
  background-color: #f8d7da;
  border: 1px solid #e6b0b6;
}

.alert {
  position: relative;
  padding: .75rem 1.25rem;
  margin-bottom: 0rem;
  border: 1px solid transparent;
  border-radius: .25rem;
}

.container.card {
  background-color: #fff;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  margin-top: 10px;
  min-width: 90%;
  min-height: 90%;
}

.add button {
  width: 200px;
  height: 40px;
  margin-top: 0px;
  margin-bottom: 20px;
  border: 1px solid #102359;
  background: #2952c4;
  color: white;
  cursor: pointer;
  text-align: center;
  transition: transform 0.2s, box-shadow 0.2s;
}

button.btn:hover {
  background-color: #0c1a61;
  transition: transform 0.2s, box-shadow 0.2s;
}

.view-trainees-button {
  background-color: #e7e7e7;
  /* Change the background color to your desired color */
  color: #000000;
  position: absolute;
  left: 40px;
  bottom: 10%;
  font-size: 14px;
  width: 140px;
  border-radius: 5px;
}

.view-trainees-button:hover {
  background-color: #cecece;
  cursor: pointer;
}

.back-button {
  position: absolute;
  bottom: 40px;
  /* Adjust the top position as needed */
  left: 60px;
  /* Adjust the right position as needed */
  width: 100px;
  border-radius: 5px;
  background-color: #c1e0bf;
  font-size: 14px;
  border: 2px solid black;
  padding: 3px;
}

.back-button:hover {
  background-color: #abc7a9;
  cursor: pointer;
}

.eye-icon {
  position: absolute;
  right: 40px;
  cursor: pointer;
  color: grey;
}

.eye-icon:hover {
  color: rgb(77, 77, 77);
}
</style>